/**
 * MobileDayAnalysisChat.jsx
 * Mobile version of the AI Daily Analysis Chat
 * Provides KI-powered nutrition analysis with interactive follow-up chat
 */
import React, { useState, useRef, useEffect } from 'react';
import { Sparkles, Send, Loader2, Calendar, TrendingUp, X } from 'lucide-react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const MobileDayAnalysisChat = ({ isOpen, onClose, portalToken, date }) => {
  const [messages, setMessages] = useState([]);
  const [inputText, setInputText] = useState('');
  const [loading, setLoading] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [includeWeek, setIncludeWeek] = useState(false);
  const scrollRef = useRef(null);

  useEffect(() => {
    if (isOpen && messages.length === 0 && !loading) {
      startAnalysis(false);
    }
    if (!isOpen) {
      setMessages([]);
      setSessionId(null);
      setIncludeWeek(false);
      setInputText('');
    }
  }, [isOpen]);

  const startAnalysis = async (withWeek) => {
    setLoading(true);
    setIncludeWeek(withWeek);
    setMessages([]);
    try {
      const res = await axios.post(`${API}/portal/nutrition/day-analysis`, {
        date,
        include_week: withWeek,
        session_id: null
      }, { headers: { Authorization: `Bearer ${portalToken}` }, timeout: 30000 });

      setSessionId(res.data.session_id);
      setMessages([{
        role: 'assistant',
        content: res.data.response
      }]);
    } catch {
      setMessages([{ role: 'assistant', content: 'Analyse konnte nicht geladen werden. Bitte versuche es erneut.' }]);
    } finally {
      setLoading(false);
    }
  };

  const sendMessage = async () => {
    if (!inputText.trim() || loading) return;
    const userMsg = inputText.trim();
    setInputText('');
    setMessages(prev => [...prev, { role: 'user', content: userMsg }]);
    setLoading(true);

    try {
      const res = await axios.post(`${API}/portal/nutrition/day-analysis`, {
        date,
        message: userMsg,
        session_id: sessionId,
        include_week: includeWeek
      }, { headers: { Authorization: `Bearer ${portalToken}` }, timeout: 30000 });

      setMessages(prev => [...prev, { role: 'assistant', content: res.data.response }]);
    } catch {
      setMessages(prev => [...prev, { role: 'assistant', content: 'Fehler bei der Anfrage. Bitte versuche es erneut.' }]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, loading]);

  const formatDate = (d) => {
    try {
      const [y, m, day] = d.split('-');
      return `${day}.${m}.${y}`;
    } catch { return d; }
  };

  if (!isOpen) return null;

  return (
    <div className="absolute inset-0 bg-black/95 z-50 flex flex-col" data-testid="mobile-day-analysis-chat">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-zinc-700/50">
        <div className="flex items-center gap-2">
          <button onClick={onClose} className="p-2 bg-zinc-800 rounded-lg">
            <X className="w-5 h-5 text-zinc-400" />
          </button>
          <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-yellow-500 to-amber-600 flex items-center justify-center">
            <Sparkles className="w-4 h-4 text-black" />
          </div>
          <div>
            <h2 className="text-sm font-semibold text-white">KI-Tagesanalyse</h2>
            <p className="text-xs text-zinc-500 flex items-center gap-1">
              <Calendar className="w-3 h-3" />
              {formatDate(date)}
            </p>
          </div>
        </div>
        <button
          onClick={() => startAnalysis(!includeWeek)}
          disabled={loading}
          className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${
            includeWeek
              ? 'bg-yellow-500/20 text-yellow-400 border border-yellow-500/30'
              : 'bg-zinc-800 text-zinc-400 hover:text-white'
          }`}
          data-testid="mobile-day-analysis-week-toggle"
        >
          <TrendingUp className="w-3 h-3 inline mr-1" />
          Woche
        </button>
      </div>

      {/* Messages */}
      <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 py-3 space-y-3" data-testid="mobile-day-analysis-messages">
        {messages.map((msg, i) => (
          <div key={i} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
            <div
              className={`max-w-[90%] rounded-2xl px-4 py-3 text-sm leading-relaxed ${
                msg.role === 'user'
                  ? 'bg-yellow-500/20 text-yellow-100 rounded-br-md'
                  : 'bg-zinc-800 text-white/90 rounded-bl-md'
              }`}
              data-testid={`mobile-day-analysis-message-${msg.role}-${i}`}
            >
              <div className="whitespace-pre-wrap" dangerouslySetInnerHTML={{
                __html: msg.content
                  .replace(/### (.*?)(\n|$)/g, '<h3 class="font-bold text-yellow-400 mt-3 mb-1">$1</h3>')
                  .replace(/## (.*?)(\n|$)/g, '<h2 class="font-bold text-yellow-400 mt-3 mb-1">$1</h2>')
                  .replace(/\*\*(.*?)\*\*/g, '<strong class="text-white">$1</strong>')
                  .replace(/^- /gm, '<span class="text-yellow-500/70 mr-1">-</span> ')
              }} />
            </div>
          </div>
        ))}

        {loading && (
          <div className="flex justify-start">
            <div className="bg-zinc-800 rounded-2xl rounded-bl-md px-4 py-3 flex items-center gap-2">
              <Loader2 className="w-4 h-4 animate-spin text-yellow-400" />
              <span className="text-sm text-zinc-500">Analysiere...</span>
            </div>
          </div>
        )}
      </div>

      {/* Input */}
      <div className="border-t border-zinc-700/50 p-3" data-testid="mobile-day-analysis-input-area">
        <div className="flex gap-2">
          <input
            value={inputText}
            onChange={(e) => setInputText(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && !e.shiftKey && sendMessage()}
            placeholder="Nachfrage stellen..."
            className="flex-1 bg-zinc-800 border border-zinc-700 rounded-xl px-3 py-2.5 text-white text-sm placeholder:text-zinc-500"
            disabled={loading || !sessionId}
            data-testid="mobile-day-analysis-input"
          />
          <button
            onClick={sendMessage}
            disabled={loading || !inputText.trim() || !sessionId}
            className="px-4 py-2.5 bg-yellow-500 rounded-xl text-black font-medium disabled:opacity-50"
            data-testid="mobile-day-analysis-send"
          >
            <Send className="w-4 h-4" />
          </button>
        </div>
        <p className="text-[10px] text-zinc-600 mt-1.5 text-center">
          KI-Analyse basiert auf deinen Eintraegen. Keine medizinische Beratung.
        </p>
      </div>
    </div>
  );
};

export default MobileDayAnalysisChat;
